import PropTypes from 'prop-types';
import React, { Fragment } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import dueDateMoment from 'moment-business-days';
import { Button } from 'react-toolbox/lib/button';
import ChecklistIntro from 'checklistIntro';
import ChecklistPhaseInfo from 'checklistPhaseInfo';
import ChecklistPhaseContactInfo from 'checklistPhaseContactInfo';
import ChecklistPhaseSpecifics from 'checklistPhaseSpecifics';
import HaltCard from './components/haltCard';
import IfiHaltCard from './components/ifiHaltCard';
import IfiClearingArea from './components/ifiClearingArea';
import isIfiPhase from '../../components/ifiUtils';
import { DropdownDiv, ContainerDiv } from 'styledComponents';
import { urls } from 'helpers/url';
import { navigate } from 'actionCreators';
import { fullAddress, smoothScrollBy } from '../../../../../../../utils';
import { getPosition } from '../../../../../../../reducers';
import {
  updatePhase,
  updatePhaseTask,
  addEventToPhase,
  overridePhaseStatus,
  clearValidation,
  removeFlagFromPhase,
} from 'actionCreators/phase';
import PhaseType from 'types/phase';
import { POTENTIAL_HALT_MESSAGES } from 'config/constants';
import S from './styles.scss';

class PhaseSchedulingChecklist extends React.Component {
  constructor(props) {
    super(props);
    this.handleBasicYesNo = this.handleBasicYesNo.bind(this);
    this.handleIntroQuestion = this.handleIntroQuestion.bind(this);
    this.handleNewDate = this.handleNewDate.bind(this);
    this.handleNewDateNote = this.handleNewDateNote.bind(this);
    this.handleAddressConfirmed = this.handleAddressConfirmed.bind(this);
    this.handleNewAddress = this.handleNewAddress.bind(this);
    this.handleHaltQuestion = this.handleHaltQuestion.bind(this);
    this.handleHaltNote = this.handleHaltNote.bind(this);
    this.handleSpecificAnswered = this.handleSpecificAnswered.bind(this);
    this.handleRemovalType = this.handleRemovalType.bind(this);
    this.handleAddAssessment = this.handleAddAssessment.bind(this);
    this.submitNewDate = this.submitNewDate.bind(this);
    this.submitNewAddress = this.submitNewAddress.bind(this);
    this.submitHaltNote = this.submitHaltNote.bind(this);
    this.submitNewSpecifics = this.submitNewSpecifics.bind(this);
    const { phase = {} } = this.props;
    const today = moment();
    this.state = {
      today,
      phase,
      phaseDate: phase.date_sold,
      isInbound: null,
      answered: null,
      canTalk: null,
      newDate: '',
      haltCardConfirmed: '',
      newDateNote: '',
      newDateError: '',
      addressConfirmed: null,
      contactInfoConfirmed: null,
      newConstructionAnswered: '',
      haltNote: '',
      powerAnswered: '',
      climateControlAnswered: '',
      waterAnswered: '',
      furnitureAnswered: '',
      furnitureOversizedAnswered: '',
      appliancesAnswered: '',
      toiletAnswered: '',
      removalAnswered: '',
      hasPets: '',
      customerResponsibilitiesReviewed: '',
      broughtMaterials: '',
      materialsNote: '',
      selectedRemovalType: '',
      showFurnitureNote: false,
      showFurnitureNoteAnswered: '',
      showAppliancesNote: false,
      showAppliancesNoteAnswered: '',
      showToiletNote: false,
      showToiletNoteAnswered: '',
      year: today.clone().format('YYYY'),
      potentialHalt: { answeredKey: '', message: '' },
      assessmentScheduling: phase.type === 'Assessment',
      errors: {},
    };
  }

  handleScrollDown = percent => smoothScrollBy({ percent })

  handleBasicYesNo = (key, val, answeredVal) =>
    this.setState({ [key]: val, [`${key}Answered`]: answeredVal, scrollDown: 18 })

  handleHaltQuestion = (key, val) => this.setState({ [key]: val },
    () => this.handleScrollDown(18))

  handleHaltNote = e => this.setState({ haltNote: e.target.value });

  handleMaterialsNote = e => this.setState({ materialsNote: e.target.value });

  handleIntroQuestion = (key, val) => {
    let newState = {};
    let scrollDown = 20;
    if (['answered', 'canTalk'].includes(key)) {
      newState[key] = val;
      if (val) scrollDown = 18;
    } else if (val) newState = { isInbound: val, answered: val, canTalk: val };
    else newState = { isInbound: val, answered: null, canTalk: null };
    this.setState(newState, () => this.handleScrollDown(scrollDown));
  }

  handleNewDate = newDate => this.setState({ newDate });

  handleNewDateNote = e => this.setState({ newDateNote: e.target.value });

  handleNewDateError = () => this.setState({ newDateError: 'You must choose a date to submit' });

  handleAddressConfirmed = addressConfirmed => this.setState({ addressConfirmed, newDateError: '' },
    () => this.handleScrollDown(addressConfirmed ? 20 : 25));

  handleContactInfoConfirmed = scrollDown => this.setState({ contactInfoConfirmed: true },
    () => this.handleScrollDown(scrollDown));

  handleNewAddress = (key, val) => {
    const { phase } = this.state;
    phase[key] = val;
    this.setState({ phase });
  }

  handlePhoneNumber = (index, key, val) => {
    const { phase } = this.state;
    phase.phone_numbers[index][key] = val;
    this.setState({ phase });
  }

  handleSpecificAnswered = (answeredKey, specificKey, val) => {
    const { phase } = this.state;
    const answeredVal = val ? 'yes' : 'no';
    const newState = { [answeredKey]: answeredVal, phase };
    let scrollDown = 20;
    if (answeredKey !== 'removalAnswered') {
      newState.phase.phase_specific[specificKey] = val;
      if (!['furnitureAnswered', 'furnitureOversizedAnswered', 'appliancesAnswered',
        'toiletAnswered'].includes(answeredKey)) {
        newState.potentialHalt = (answeredVal === 'yes') ? { answeredKey: '', message: '' }
          : { answeredKey, message: POTENTIAL_HALT_MESSAGES[answeredKey] };
        scrollDown = 15;
      }
    }
    this.setState(newState, () => this.handleScrollDown(scrollDown));
  }

  handleRemovalType = (val) => {
    const { phase } = this.props;
    phase.phase_specific[val] = true;
    this.setState({ phase, selectedRemovalType: val }, () => this.handleScrollDown(20));
  }

  handleAddAssessment = () =>
    this.props.navigate(urls.newAppointment(this.props.phase.id, 'assessment'));

  submitNewDate = (answered = true) => {
    const { phase: { id, due_at, phase_specific }, phase, newDate, newDateNote } = this.state;
    const new_due_date = answered ? newDate || '' : dueDateMoment().add(1, 'day').toISOString();
    const editedDateNote = answered ? newDateNote
      : 'Due date moved to next business day due to the customer not answering';
    if (answered && !new_due_date) this.handleNewDateError();
    else {
      const updatePhaseArg = { id, due_at: new_due_date };
      const updateAddtlArgs = { actionType: 'update_due_at' };
      if (answered) updatePhaseArg.phase_specific_attributes = phase_specific;
      else updateAddtlArgs.communication = 'SendScheduleAttemptMessage';
      const noteProps = {
        name: 'Due Date Updated',
        kind: 'due_date_updated',
        data: { newDateNote: editedDateNote, old_due_date: due_at, new_due_date },
      };
      this.props.updatePhase(updatePhaseArg, updateAddtlArgs)
        .then(() => this.props.addEventToPhase(id, noteProps))
        .then(() => this.props.navigate(urls.phaseHistory(phase)))
        .catch(() => { });
    }
  };

  submitNewAddress = () => {
    const { phase } = this.state;
    if (phase.date_sold || '') delete phase.date_sold;
    this.props.updatePhase(phase, {})
      .then(() => this.handleAddressConfirmed(null))
      .catch(() => { });
  }

  submitContactInfo = () => {
    const { phase, phase: { id, phone_numbers } } = this.state;
    let setAutoComText = false;
    const phoneNumbersAttributes = phone_numbers.map((phone, i) => {
      if (phone.number.trim() === '') {
        if (!phone.id) return i > 0 ? null : phone;
        return { ...phone, _destroy: true };
      }
      if (phone.ok_to_text === true && phase.communication_method !== 'text') setAutoComText = true;
      return phone;
    }).filter(n => !!n);
    this.props.updatePhase({
      id,
      phone_numbers_attributes: phoneNumbersAttributes,
      ...(setAutoComText && { communication_method: 'text' }),
    }, {})
      .then(() => this.handleContactInfoConfirmed(20))
      .catch(() => { });
  }

  submitHaltNote = () => {
    const { phase, phase: { id }, haltNote } = this.state;
    const noteProps = {
      name: 'Note',
      kind: 'note',
      data: { note: haltNote },
    };
    this.props.addEventToPhase(id, noteProps)
      .then(() => this.props.navigate(urls.phaseHistory(phase)))
      .catch(() => { });
  }

  submitNewSpecifics = () => {
    const { phase, phase: { id, phase_specific } } = this.state;
    this.props.updatePhase({ id, phase_specific_attributes: phase_specific }, {})
      .then(() => {
        this.props.updatePhaseTask(
          id, 'scheduling_checklist', { task: { status: 'complete' } },
        );
      })
      .then(() => this.props.navigate(urls.phaseAppointments(phase)))
      .catch(() => { });
  }

  ifiContractorNotes = () => {
    const { phase: { contractor_notes }, hasPets, broughtMaterials, materialsNote } = this.state;
    const materialsLine = (broughtMaterials === 'yes' && materialsNote)
      ? `\n\n-->> MATERIALS BROUGHT HOME: ${materialsNote} <<--` : '';
    return `${contractor_notes || ''}

-->> PETS PRESENT: ${hasPets === 'yes' ? 'Yes' : 'No'} <<--

-->> BROUGHT MATERIALS HOME: ${broughtMaterials === 'yes' ? 'Yes' : 'No'} <<--${materialsLine}`;
  }

  submitIfiNewSpecifics = () => {
    const { phase, phase: { id, phase_specific } } = this.state;
    const payload = {
      ...phase,
      contractor_notes: this.ifiContractorNotes(),
      phase_specific_attributes: phase_specific,
    };
    if (payload.date_sold || '') delete payload.date_sold;
    this.props.updatePhase(payload, {})
      .then(() => this.props.updatePhaseTask(
        id, 'scheduling_checklist', { task: { status: 'complete' } },
      ))
      .then(() => this.props.navigate(urls.phaseAppointments(phase)))
      .catch(() => { });
  }

  render = () => {
    const {
      phase,
      isInbound,
      answered,
      canTalk,
      newDate,
      newDateNote,
      newDateError,
      addressConfirmed,
      contactInfoConfirmed,
      newConstructionAnswered,
      haltNote,
      powerAnswered,
      climateControlAnswered,
      waterAnswered,
      furnitureAnswered,
      furnitureOversizedAnswered,
      appliancesAnswered,
      toiletAnswered,
      removalAnswered,
      selectedRemovalType,
      hasPets,
      customerResponsibilitiesReviewed,
      broughtMaterials,
      materialsNote,
      showFurnitureNote,
      showFurnitureNoteAnswered,
      showAppliancesNote,
      showAppliancesNoteAnswered,
      showToiletNote,
      showToiletNoteAnswered,
      errors,
      potentialHalt: { message },
      assessmentScheduling,
    } = this.state;
    const { user, position } = this.props;
    const isIfi = isIfiPhase(phase);
    return (<div>
      {!assessmentScheduling && <Fragment>
        <ChecklistIntro
          user={user}
          phase={phase}
          isInbound={isInbound}
          answered={answered}
          canTalk={canTalk}
          newDate={newDate}
          newDateNote={newDateNote}
          newDateError={newDateError}
          handleIntroQuestion={this.handleIntroQuestion}
          handleNewDate={this.handleNewDate}
          handleNewDateNote={this.handleNewDateNote}
          submitNewDate={this.submitNewDate}
          errors={errors}
        />
        <br />
      </Fragment>}
      {(canTalk || assessmentScheduling) && <ContainerDiv>
        <DropdownDiv condition={canTalk || assessmentScheduling}>
          <ChecklistPhaseInfo
            phase={phase}
            assessmentScheduling={assessmentScheduling}
            originalAddress={fullAddress(phase)}
            addressConfirmed={addressConfirmed}
            handleAddressConfirmed={this.handleAddressConfirmed}
            handleNewAddress={this.handleNewAddress}
            submitNewAddress={this.submitNewAddress}
          />
        </DropdownDiv>
      </ContainerDiv>}
      {(addressConfirmed || assessmentScheduling) && <ContainerDiv>
        <DropdownDiv condition={addressConfirmed || assessmentScheduling}>
          <ChecklistPhaseContactInfo
            assessmentScheduling={assessmentScheduling}
            phone_numbers={phase.phone_numbers}
            contactInfoConfirmed={contactInfoConfirmed}
            handlePhoneNumber={this.handlePhoneNumber}
            submitContactInfo={this.submitContactInfo}
          />
        </DropdownDiv>
      </ContainerDiv>}
      {!assessmentScheduling && <Fragment>
        {contactInfoConfirmed && <ContainerDiv>
          <DropdownDiv condition={contactInfoConfirmed}>
            {isIfi ? (
              <IfiHaltCard
                powerAnswered={powerAnswered}
                handleSpecificAnswered={this.handleSpecificAnswered}
                haltMessage={message}
                newDate={newDate}
                newDateNote={newDateNote}
                newDateError={newDateError}
                handleNewDate={this.handleNewDate}
                handleNewDateNote={this.handleNewDateNote}
                submitNewDate={this.submitNewDate}
                errors={errors}
              />
            ) : (
              <HaltCard
                handleHaltQuestion={this.handleHaltQuestion}
                newConstructionAnswered={newConstructionAnswered}
                handleHaltNote={this.handleHaltNote}
                haltNote={haltNote}
                submitHaltNote={this.submitHaltNote}
              />
            )}
          </DropdownDiv>
        </ContainerDiv>}
        <br />
        {isIfi ? (
          powerAnswered === 'yes' && <ContainerDiv>
            <DropdownDiv condition={powerAnswered === 'yes'}>
              <IfiClearingArea
                hasPets={hasPets}
                customerResponsibilitiesReviewed={customerResponsibilitiesReviewed}
                broughtMaterials={broughtMaterials}
                materialsNote={materialsNote}
                handleHaltQuestion={this.handleHaltQuestion}
                handleMaterialsNote={this.handleMaterialsNote}
                submitNewSpecifics={this.submitIfiNewSpecifics}
              />
            </DropdownDiv>
          </ContainerDiv>
        ) : (
          newConstructionAnswered === 'no' && <ContainerDiv>
            <DropdownDiv condition={newConstructionAnswered === 'no'}>
              <ChecklistPhaseSpecifics
                position={position}
                phase={phase}
                powerAnswered={powerAnswered}
                climateControlAnswered={climateControlAnswered}
                waterAnswered={waterAnswered}
                furnitureAnswered={furnitureAnswered}
                furnitureOversizedAnswered={furnitureOversizedAnswered}
                appliancesAnswered={appliancesAnswered}
                toiletAnswered={toiletAnswered}
                removalAnswered={removalAnswered}
                selectedRemovalType={selectedRemovalType}
                newDate={newDate}
                newDateNote={newDateNote}
                newDateError={newDateError}
                haltMessage={message}
                showFurnitureNote={showFurnitureNote}
                showFurnitureNoteAnswered={showFurnitureNoteAnswered}
                showAppliancesNote={showAppliancesNote}
                showAppliancesNoteAnswered={showAppliancesNoteAnswered}
                showToiletNote={showToiletNote}
                showToiletNoteAnswered={showToiletNoteAnswered}
                handleBasicYesNo={this.handleBasicYesNo}
                handleSpecificAnswered={this.handleSpecificAnswered}
                handleRemovalType={this.handleRemovalType}
                handleNewDate={this.handleNewDate}
                handleNewDateNote={this.handleNewDateNote}
                submitNewSpecifics={this.submitNewSpecifics}
                submitNewDate={this.submitNewDate}
                errors={errors}
              />
            </DropdownDiv>
          </ContainerDiv>
        )}

      </Fragment>}
      {assessmentScheduling && <Button
        label="PROCEED TO SCHEDULING"
        flat inverse ripple
        disabled={!contactInfoConfirmed}
        className={S.saveButton}
        onClick={() => this.handleAddAssessment()}
      />}
    </div>);
  }
}

PhaseSchedulingChecklist.propTypes = {
  phase: PhaseType.isRequired,
  position: PropTypes.string.isRequired,
  user: PropTypes.string.isRequired,
  navigate: PropTypes.func.isRequired,
  updatePhase: PropTypes.func.isRequired,
  updatePhaseTask: PropTypes.func.isRequired,
  addEventToPhase: PropTypes.func.isRequired,
  overridePhaseStatus: PropTypes.func.isRequired,
  clearValidation: PropTypes.func.isRequired,
  removeFlagFromPhase: PropTypes.func.isRequired,
};

export default connect(
  state => ({
    phase: state.phase.details,
    user: state.user.name,
    position: getPosition(state),
  }), {
    navigate,
    updatePhase,
    updatePhaseTask,
    addEventToPhase,
    overridePhaseStatus,
    clearValidation,
    removeFlagFromPhase,
  },
)(PhaseSchedulingChecklist);



// WEBPACK FOOTER //
// ./client/app/screens/main/screens/phases/phaseShow/tabs/phaseSchedulingChecklist/index.jsx